import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import axiosInstance from "../AxiosInstance";
import { getSavedEposRetailer, saveEposRetailer } from "../Utilities/eposRetailerPreference";
import EposTabs from "./EposTabs";

interface ProductOption {
    productId: number;
    skuNumber: string;
    skuDescription: string;
}

interface IgnoreProductOption {
    type: "ignore";
}

type ProductSelectionOption = ProductOption | IgnoreProductOption;
type MappingType = "explicit" | "skuMatch" | "ignored" | "unmapped";

const ignoreProductOption: IgnoreProductOption = { type: "ignore" };

const isIgnoreProductOption = (option: ProductSelectionOption): option is IgnoreProductOption =>
    "type" in option && option.type === "ignore";

interface EposProductMapping {
    eposProductMappingId: number | null;
    retailer: number;
    eposSkuNumber: string;
    eposSkuDescription: string;
    lastSeenWeekEndingDate: string;
    productId: number | null;
    productSkuNumber: string | null;
    productDescription: string | null;
    mappingType: MappingType;
}

interface EposProductMappingPage {
    items: EposProductMapping[];
    totalCount: number;
    unmappedCount: number;
    page: number;
    pageSize: number;
}

const pageSize = 25;

const errorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; detail?: string } | undefined;
        return data?.message ?? data?.detail ?? fallback;
    }

    return fallback;
};

export default function EposProductMappingsPage() {
    const [retailer, setRetailer] = useState(getSavedEposRetailer);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [mappings, setMappings] = useState<EposProductMapping[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [unmappedCount, setUnmappedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [error, setError] = useState("");
    const [productsError, setProductsError] = useState("");
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const mappingRequestController = useRef<AbortController | null>(null);
    const activeRetailerRef = useRef(retailer);

    useEffect(() => {
        let cancelled = false;

        const loadProducts = async () => {
            setProductsLoading(true);
            setProductsError("");

            try {
                const response = await axiosInstance.get<ProductOption[]>("/products");
                if (!cancelled) {
                    setProducts(response.data);
                }
            } catch (requestError) {
                if (!cancelled) {
                    setProductsError(errorMessage(requestError, "Products could not be loaded."));
                }
            } finally {
                if (!cancelled) {
                    setProductsLoading(false);
                }
            }
        };

        void loadProducts();

        return () => {
            cancelled = true;
        };
    }, []);

    const loadMappings = useCallback(async () => {
        mappingRequestController.current?.abort();
        const controller = new AbortController();
        mappingRequestController.current = controller;
        setLoading(true);
        setError("");

        try {
            const response = await axiosInstance.get<EposProductMappingPage>("/epos/product-mappings", {
                params: {
                    retailer,
                    unmappedOnly: false,
                    page,
                    pageSize
                },
                signal: controller.signal
            });
            if (!controller.signal.aborted) {
                setMappings(response.data.items);
                setTotalCount(response.data.totalCount);
                setUnmappedCount(response.data.unmappedCount);
            }
        } catch (requestError) {
            if (!controller.signal.aborted) {
                setError(errorMessage(requestError, "Product links could not be loaded."));
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [page, retailer]);

    useEffect(() => {
        void loadMappings();

        return () => mappingRequestController.current?.abort();
    }, [loadMappings]);

    const productLookup = useMemo(
        () => new Map(products.map(product => [product.productId, product])),
        [products]
    );

    const mappingKey = (mapping: EposProductMapping) =>
        `${mapping.retailer}:${mapping.eposSkuNumber}`;

    const refreshAfterMutation = async (retailerAtStart: string) => {
        if (activeRetailerRef.current !== retailerAtStart) return;
        await loadMappings();
    };

    const saveMapping = async (mapping: EposProductMapping, productId: number) => {
        const retailerAtStart = retailer;
        const key = mappingKey(mapping);
        setSavingKey(key);
        setError("");

        try {
            await axiosInstance.put("/epos/product-mappings", {
                retailer: mapping.retailer,
                eposSkuNumber: mapping.eposSkuNumber,
                productId
            });
            if (activeRetailerRef.current !== retailerAtStart) return;
            setSuccessMessage("Product linked.");
            await refreshAfterMutation(retailerAtStart);
        } catch (requestError) {
            if (activeRetailerRef.current === retailerAtStart)
                setError(errorMessage(requestError, "The product could not be linked."));
        } finally {
            if (activeRetailerRef.current === retailerAtStart)
                setSavingKey(null);
        }
    };

    const ignoreMapping = async (mapping: EposProductMapping) => {
        const retailerAtStart = retailer;
        const key = mappingKey(mapping);
        setSavingKey(key);
        setError("");

        try {
            await axiosInstance.put("/epos/product-mappings/ignore", {
                retailer: mapping.retailer,
                eposSkuNumber: mapping.eposSkuNumber
            });
            if (activeRetailerRef.current !== retailerAtStart) return;
            setSuccessMessage("Product ignored.");
            await refreshAfterMutation(retailerAtStart);
        } catch (requestError) {
            if (activeRetailerRef.current === retailerAtStart)
                setError(errorMessage(requestError, "The product could not be ignored."));
        } finally {
            if (activeRetailerRef.current === retailerAtStart)
                setSavingKey(null);
        }
    };

    const removeMapping = async (mapping: EposProductMapping) => {
        if (mapping.eposProductMappingId == null) {
            return;
        }

        const retailerAtStart = retailer;
        const key = mappingKey(mapping);
        setSavingKey(key);
        setError("");

        try {
            await axiosInstance.delete(`/epos/product-mappings/${mapping.eposProductMappingId}`);
            if (activeRetailerRef.current !== retailerAtStart) return;
            setSuccessMessage("Product unlinked.");
            await refreshAfterMutation(retailerAtStart);
        } catch (requestError) {
            if (activeRetailerRef.current === retailerAtStart)
                setError(errorMessage(requestError, "The product could not be unlinked."));
        } finally {
            if (activeRetailerRef.current === retailerAtStart)
                setSavingKey(null);
        }
    };

    const pageCount = Math.ceil(totalCount / pageSize);

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs
                unmappedCount={unmappedCount}
                retailer={retailer}
                onRetailerChange={nextRetailer => {
                    const selectedRetailer = saveEposRetailer(nextRetailer);
                    activeRetailerRef.current = selectedRetailer;
                    setError("");
                    setSuccessMessage("");
                    setRetailer(selectedRetailer);
                    setPage(1);
                }}
            />

            {productsError && <Alert severity="error" sx={{ mb: 3 }}>{productsError}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Card variant="outlined">
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{ px: { xs: 2, sm: 2.5 }, py: 1, borderBottom: 1, borderColor: "divider" }}
                >
                    <Typography color="text.secondary" variant="body2" sx={{ pb: 1 }}>
                        {totalCount} {totalCount === 1 ? "product" : "products"}
                    </Typography>
                </Stack>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                        <CircularProgress />
                    </Box>
                ) : mappings.length === 0 ? (
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            EPOS products will appear here after an import completes.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 900 }} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>EPOS product</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Last seen</TableCell>
                                        <TableCell sx={{ fontWeight: 700, minWidth: 360 }}>Linked to</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mappings.map(mapping => {
                                        const key = mappingKey(mapping);
                                        const isSaving = savingKey === key;
                                        const selectedProduct = mapping.mappingType === "ignored"
                                            ? ignoreProductOption
                                            : mapping.productId == null
                                                ? null
                                                : productLookup.get(mapping.productId) ?? {
                                                    productId: mapping.productId,
                                                    skuNumber: mapping.productSkuNumber ?? "",
                                                    skuDescription: mapping.productDescription ?? ""
                                                };

                                        return (
                                            <TableRow key={key}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {mapping.eposSkuNumber}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {mapping.eposSkuDescription}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {dayjs(mapping.lastSeenWeekEndingDate).format("D MMM YYYY")}
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete<ProductSelectionOption, false, boolean, false>
                                                        options={[ignoreProductOption, ...products]}
                                                        value={selectedProduct}
                                                        loading={productsLoading || isSaving}
                                                        disabled={productsLoading || isSaving}
                                                        disableClearable={mapping.productId != null || mapping.mappingType === "ignored"}
                                                        isOptionEqualToValue={(option, value) =>
                                                            isIgnoreProductOption(option) || isIgnoreProductOption(value)
                                                                ? isIgnoreProductOption(option) && isIgnoreProductOption(value)
                                                                : option.productId === value.productId
                                                        }
                                                        getOptionLabel={product => isIgnoreProductOption(product)
                                                            ? "- Ignore -"
                                                            : `${product.skuNumber} - ${product.skuDescription}`}
                                                        onChange={(_event, value) => {
                                                            if (value) {
                                                                if (isIgnoreProductOption(value)) {
                                                                    void ignoreMapping(mapping);
                                                                } else {
                                                                    void saveMapping(mapping, value.productId);
                                                                }
                                                            }
                                                        }}
                                                        noOptionsText="No matching products"
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    "aria-label": "Select product"
                                                                }}
                                                                placeholder="Select product..."
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {isSaving ? <CircularProgress size={18} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {mapping.mappingType === "explicit" ? (
                                                        <Chip label="Linked" color="success" size="small" />
                                                    ) : mapping.mappingType === "skuMatch" ? (
                                                        <Chip label="Linked by SKU" color="info" size="small" />
                                                    ) : mapping.mappingType === "ignored" ? (
                                                        <Chip label="Ignored" color="default" size="small" />
                                                    ) : (
                                                        <Chip label="Unlinked" color="warning" size="small" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {mapping.eposProductMappingId != null && (
                                                        <Button
                                                            size="small"
                                                            color="inherit"
                                                            disabled={isSaving}
                                                            onClick={() => void removeMapping(mapping)}
                                                        >
                                                            Unlink
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {pageCount > 1 && (
                            <Stack alignItems="center" sx={{ p: 2 }}>
                                <Pagination
                                    count={pageCount}
                                    page={page}
                                    onChange={(_event, value) => setPage(value)}
                                    color="primary"
                                />
                            </Stack>
                        )}
                    </>
                )}
            </Card>

            {successMessage && (
                <Alert
                    severity="success"
                    onClose={() => setSuccessMessage("")}
                    sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}
                >
                    {successMessage}
                </Alert>
            )}
        </Box>
    );
}
